import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, getQueryFn } from "@/lib/api";
import { isUnauthorizedError } from "@/lib/authUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AppShell, PageHeader } from "@/components/app";
import { SurveyEditor } from "@/components/SurveyEditor";
import { SurveyPreview } from "@/components/SurveyPreview";
import { Survey } from "@/types/survey";
import { 
  Save, 
  Eye, 
  Settings, 
  Rocket, 
  Download, 
  CheckCircle,
  Copy,
  Check,
  Globe,
  Users,
  Anchor,
  Info,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  AlertCircle
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function SurveyBuilder() {
  const { id } = useParams<{ id: string }>();
  const surveyId = parseInt(id || "0");
  const { isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [copied, setCopied] = useState(false);
  // Track if user has made local changes that haven't been saved yet
  const [isDirty, setIsDirty] = useState(false);
  // Track the last server version to detect when we should sync
  const [lastServerVersion, setLastServerVersion] = useState<string | null>(null);
  // Publish confirmation modal state
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  // Fetch survey data - always refetch to ensure fresh data
  const { data: surveyData, isLoading: surveyLoading, error } = useQuery<Survey | null>({
    queryKey: [`/api/surveys/${surveyId}`],
    queryFn: getQueryFn<Survey | null>({ on401: "returnNull" }),
    enabled: !!surveyId && !authLoading,
    retry: false,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    refetchOnMount: "always", // Always refetch when component mounts
  });

  // Auto-save mutation - destructure for stable references
  const { mutate: saveChanges, isPending: isSaving } = useMutation({
    mutationFn: async (updatedSurvey: Partial<Survey>) => {
      const response = await apiRequest("PUT", `/api/surveys/${surveyId}`, updatedSurvey);
      return response.json();
    },
    onSuccess: (savedSurvey) => {
      setLastSaved(new Date());
      setIsDirty(false);
      // Update the server version to match what we just saved
      if (savedSurvey?.updatedAt) {
        setLastServerVersion(savedSurvey.updatedAt);
      }
      // Update the cache directly with saved data (prevents flicker)
      queryClient.setQueryData([`/api/surveys/${surveyId}`], savedSurvey);
      // Invalidate the list for the projects page
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
    },
    onError: (error) => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Error",
        description: "Failed to save survey",
        variant: "destructive",
      });
    },
  });

  // Export survey mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("GET", `/api/surveys/${surveyId}`);
      return response.json();
    },
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.title.replace(/[^a-zA-Z0-9]/g, "_")}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Error",
        description: "Failed to export survey",
        variant: "destructive",
      });
    },
  });

  // Publish survey mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/surveys/${surveyId}/publish`);
      return response.json();
    },
    onSuccess: (data) => {
      setSurvey(prev => prev ? { 
        ...prev, 
        status: "published", 
        shareToken: data.shareToken,
        publishedAt: data.publishedAt 
      } : null);
      queryClient.invalidateQueries({ queryKey: [`/api/surveys/${surveyId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
      toast({
        title: "🎉 Survey Published!",
        description: "Your survey is now live and accepting responses.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Error",
        description: "Failed to publish survey. Make sure your survey has at least one question.",
        variant: "destructive",
      });
    },
  });

  // Unpublish survey mutation
  const unpublishMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/surveys/${surveyId}/unpublish`);
      return response.json();
    },
    onSuccess: () => {
      setSurvey(prev => prev ? { ...prev, status: "draft" } : null);
      queryClient.invalidateQueries({ queryKey: [`/api/surveys/${surveyId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/surveys"] });
      toast({
        title: "Survey Unpublished",
        description: "Your survey is now in draft mode.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error as Error)) {
        toast({
          title: "Unauthorized",
          description: "Please log in to continue.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      toast({
        title: "Error",
        description: "Failed to unpublish survey",
        variant: "destructive",
      });
    },
  });

  // Copy share link to clipboard
  const copyShareLink = () => {
    if (survey?.shareToken) {
      const shareUrl = `${window.location.origin}/s/${survey.shareToken}`;
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Link Copied!",
        description: "Share this link with your respondents.",
      });
    }
  };

  // Track the last synced server data ID to avoid redundant syncs
  const lastSyncedDataIdRef = useRef<string | null>(null);
  
  // Initialize/sync survey from fetched data
  useEffect(() => {
    if (!surveyData) return;
    
    // Create a unique ID for this data version
    const dataId = surveyData.updatedAt || surveyData.id?.toString() || null;
    
    // Skip if we've already synced this exact data
    if (dataId === lastSyncedDataIdRef.current) return;
    
    const serverVersion = surveyData.updatedAt || null;
    
    // Use functional update to check current state without adding to deps
    setSurvey(currentSurvey => {
      // Sync from server if:
      // 1. Initial load (survey is null), OR
      // 2. Server version changed AND we don't have unsaved local changes
      // Note: We check isDirty and lastServerVersion via closure - they're stable for this decision
      const shouldSync = !currentSurvey || (!isDirty && serverVersion !== lastServerVersion);
      
      if (!shouldSync) return currentSurvey;
      
      // Mark this data as synced
      lastSyncedDataIdRef.current = dataId;
      
      // Ensure questions array has IDs if missing
      const questionsWithIds = (surveyData.questions || []).map((q: Survey['questions'][number], index: number) => ({
        ...q,
        id: q.id || `question-${index + 1}`
      }));
      
      // Ensure questions array, settings, and status exist
      return {
        ...surveyData,
        questions: questionsWithIds,
        status: surveyData.status || 'draft',
        settings: {
          anonymous: true,
          allowMultipleResponses: false,
          ...surveyData.settings
        }
      };
    });
    
    // Update version tracking if we synced
    if (!isDirty) {
      setLastServerVersion(serverVersion);
      setIsDirty(false);
    }
  }, [surveyData, isDirty, lastServerVersion]);

  // Handle unauthorized errors
  useEffect(() => {
    if (error && isUnauthorizedError(error as Error)) {
      toast({
        title: "Unauthorized",
        description: "Please log in to continue.",
        variant: "destructive",
      });
      navigate("/auth");
    }
  }, [error, toast, navigate]);

  // Auto-save on survey changes (only when dirty and not already saving)
  useEffect(() => {
    // Only auto-save if:
    // 1. Survey exists with questions
    // 2. User has made changes (isDirty)
    // 3. Not already saving
    if (survey && isDirty && !isSaving) {
      const timeout = setTimeout(() => {
        saveChanges({
          title: survey.title,
          description: survey.description,
          questions: survey.questions,
          settings: survey.settings,
        });
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [survey, isDirty, isSaving, saveChanges]);

  // Quota validation warnings (Phase 5.5 + Phase 6.5/6.6/6.7)
  // Must be before early returns to follow Rules of Hooks
  const quotaWarnings = useMemo(() => {
    if (!survey) return [];
    const warnings: string[] = [];
    const questions = survey.questions;
    
    if (survey.settings?.quotasEnabled) {
      const hasQuotaRules = questions.some(q =>
        q.segmentationRules?.some(r => r.quotaMax || r.quotaMin)
      );
      
      if (!hasQuotaRules) {
        warnings.push("Quotas are enabled but no segmentation rules have quota limits set.");
      }
      
      // Check for invalid quotaMin > quotaMax
      questions.forEach(q => {
        q.segmentationRules?.forEach((r, idx) => {
          if (r.quotaMin && r.quotaMax && r.quotaMin > r.quotaMax) {
            warnings.push(
              `${q.originalQuestionNumber || 'Q?'} Rule ${idx + 1}: quotaMin (${r.quotaMin}) > quotaMax (${r.quotaMax})`
            );
          }
        });
      });
      
      // Phase 6.5: Check for segmentation rules on questions with display conditions
      questions.forEach((q, idx) => {
        const hasSegmentationRules = q.segmentationRules?.some(r => r.segmentName?.trim());
        const hasDisplayConditions = q.displayConditions && q.displayConditions.length > 0;
        
        if (hasSegmentationRules && hasDisplayConditions) {
          const qNum = q.originalQuestionNumber || `Q${idx + 1}`;
          warnings.push(
            `${qNum} has both segmentation rules and display conditions. If the question is hidden, the quota check will be skipped.`
          );
        }
      });
      
      // Phase 6.6: Check for skip logic that bypasses segmentation questions
      questions.forEach((q, idx) => {
        // Check all skip logic rules on this question
        const skipRules = [
          ...(q.skipLogic || []),  // Question-level skip rules
          // Option-level skip logic (each OptionLogic may have a skipLogic property)
          ...(q.optionLogic?.filter(ol => ol.skipLogic).map(ol => ol.skipLogic).filter((s): s is NonNullable<typeof s> => s !== null && s !== undefined) || []),
        ];
        
        skipRules.forEach(rule => {
          if (rule.action === "skip_to_question" && rule.targetQuestionId) {
            const targetIdx = questions.findIndex(tq => tq.id === rule.targetQuestionId);
            
            // Check if any question between current and target has segmentation rules
            for (let i = idx + 1; i < targetIdx; i++) {
              const skippedQ = questions[i];
              if (skippedQ.segmentationRules?.some(r => r.segmentName?.trim())) {
                const qNum = q.originalQuestionNumber || `Q${idx + 1}`;
                const skippedNum = skippedQ.originalQuestionNumber || `Q${i + 1}`;
                warnings.push(
                  `Skip logic on ${qNum} jumps over ${skippedNum}, which has segmentation rules. Affected quotas will not be checked for skipped respondents.`
                );
                break; // One warning per skip rule is enough
              }
            }
          }
        });
      });
      
      // Phase 6.7: Check for question randomization + segmentation rules conflict
      // Note: Option randomization (randomizeOptions) is safe because segmentation
      // conditions match by option ID, not display position. Only question-level
      // randomization poses a risk when segmentation rules reference other questions.
      const hasQuestionRandomization = survey.settings?.randomizeQuestions;
      
      const hasSegmentationRules = questions.some(q => 
        q.segmentationRules?.some(r => r.segmentName?.trim())
      );
      
      if (hasQuestionRandomization && hasSegmentationRules) {
        warnings.push(
          "Question order randomization is enabled with segmentation rules. Ensure segmentation conditions only reference questions that appear before them in ALL possible orderings, or anchor quota-critical questions to fixed positions."
        );
      }
    }
    
    return warnings;
  }, [survey]);

  // Loading state
  if (authLoading || surveyLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Error state - Survey not found (per guide section 3.8)
  if (error || !survey || !survey.questions) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Survey Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The survey you're looking for doesn't exist or you don't have access to it.
            </p>
            <Link to="/projects">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Projects
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSurveyChange = (updatedSurvey: Survey) => {
    setSurvey(updatedSurvey);
    setIsDirty(true);
  };

  return (
    <AppShell maxWidth="wide">
      <PageHeader
        title={survey.title || "Survey Builder"}
        subtitle={`${survey.questions?.length || 0} questions`}
        breadcrumbs={[
          { label: "Projects", href: "/projects" },
          { label: survey.title || "Untitled Survey" }
        ]}
        actions={
          <div className="flex items-center gap-2">
            {/* Save status indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : isDirty ? (
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Unsaved changes
                </span>
              ) : lastSaved ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>Saved</span>
                </>
              ) : null}
            </div>
            
            {/* Action buttons */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                // Scroll to preview section in sidebar
                document.getElementById('survey-preview')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            {survey.status !== "published" && (
              <Button 
                size="sm"
                onClick={() => setShowPublishConfirm(true)}
                disabled={publishMutation.isPending || survey.questions.length === 0}
              >
                <Rocket className="h-4 w-4 mr-2" />
                Publish
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="lg:w-2/3">
          <Card className="shadow-lg">
            <CardHeader className="border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-semibold text-foreground">Survey Editor</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {survey.questions?.length || 0} questions extracted and classified
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isSaving || !isDirty}
                    onClick={() => saveChanges({
                      title: survey.title,
                      description: survey.description,
                      questions: survey.questions,
                      settings: survey.settings,
                    })}
                    aria-label="Save survey"
                    title="Save now"
                  >
                    <Save className="h-4 w-4" />
                  </Button>
                  {isSaving ? (
                    <span className="text-xs text-muted-foreground flex items-center">
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      Saving...
                    </span>
                  ) : isDirty ? (
                    <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center">
                      Unsaved changes
                    </span>
                  ) : lastSaved ? (
                    <span className="text-xs text-green-600 dark:text-green-400 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Saved
                    </span>
                  ) : null}
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6">
                {/* Quota Validation Warnings (Phase 5.5) */}
                {quotaWarnings.length > 0 && (
                  <Alert className="mb-4 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertTitle className="text-amber-800 dark:text-amber-200">Quota Configuration Warnings</AlertTitle>
                    <AlertDescription className="text-amber-700 dark:text-amber-300">
                      <ul className="list-disc list-inside mt-2">
                        {quotaWarnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                <SurveyEditor survey={survey} onChange={handleSurveyChange} />
              </CardContent>
            </Card>
          </div>

          <div className="lg:w-1/3">
            <div className="sticky top-8">
              <Card id="survey-preview" className="shadow-lg mb-6 scroll-mt-8">
                <CardHeader className="border-b border-border">
                  <CardTitle className="font-semibold text-foreground flex items-center">
                    <Eye className="h-5 w-5 mr-2 text-primary" />
                    Survey Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <SurveyPreview survey={survey} />
                </CardContent>
              </Card>

              <Card className="shadow-lg mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Survey Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Survey Title */}
                  <div className="space-y-2">
                    <Label htmlFor="title">Survey Title</Label>
                    <Input
                      id="title"
                      value={survey.title}
                      onChange={(e) => {
                        setSurvey({ ...survey, title: e.target.value });
                        setIsDirty(true);
                      }}
                      placeholder="Enter survey title"
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={survey.description || ""}
                      onChange={(e) => {
                        setSurvey({ ...survey, description: e.target.value });
                        setIsDirty(true);
                      }}
                      placeholder="Brief description of your survey..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Anonymous Responses */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="anonymous">Anonymous Responses</Label>
                      <p className="text-xs text-muted-foreground">
                        Don't collect respondent identifiers
                      </p>
                    </div>
                    <Switch
                      id="anonymous"
                      checked={survey.settings?.anonymous || false}
                      onCheckedChange={(checked) => {
                        setSurvey({
                          ...survey,
                          settings: { ...survey.settings, anonymous: checked }
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>
                  
                  {/* Enable Quotas */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5 flex items-center gap-2">
                      <div>
                        <Label htmlFor="quotas-enabled">Enable Quotas</Label>
                        <p className="text-xs text-muted-foreground">
                          Set response limits by demographic
                        </p>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs text-sm">
                              Quotas allow you to limit responses based on segmentation rules. 
                              Configure quotas after enabling.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Switch
                      id="quotas-enabled"
                      checked={survey.settings?.quotasEnabled ?? false}
                      onCheckedChange={(checked) => {
                        setSurvey({
                          ...survey,
                          settings: { ...survey.settings, quotasEnabled: checked }
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>
                  
                  {/* Randomize Question Order */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="randomize-questions">Randomize Question Order</Label>
                      <p className="text-xs text-muted-foreground">
                        Shuffle questions for each respondent
                      </p>
                    </div>
                    <Switch
                      id="randomize-questions"
                      checked={survey.settings?.randomizeQuestions || false}
                      onCheckedChange={(checked) => {
                        setSurvey({
                          ...survey,
                          settings: { 
                            ...survey.settings, 
                            randomizeQuestions: checked,
                            // Clear anchored questions when disabling randomization
                            anchoredQuestionIds: checked ? survey.settings?.anchoredQuestionIds : []
                          }
                        });
                        setIsDirty(true);
                      }}
                    />
                  </div>
                  
                  {/* Anchored Questions (conditional) */}
                  {survey.settings?.randomizeQuestions && survey.questions.length > 0 && (
                    <div className="space-y-3 pt-2 border-t border-border">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2">
                          <Anchor className="h-4 w-4" />
                          Anchored Questions
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Keep these questions in their original position
                        </p>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {survey.questions.map((q, idx) => (
                          <label
                            key={q.id}
                            className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-2 rounded"
                          >
                            <Checkbox
                              checked={survey.settings?.anchoredQuestionIds?.includes(q.id) || false}
                              onCheckedChange={(checked) => {
                                const current = survey.settings?.anchoredQuestionIds || [];
                                const updated = checked
                                  ? [...current, q.id]
                                  : current.filter(id => id !== q.id);
                                setSurvey({
                                  ...survey,
                                  settings: { 
                                    ...survey.settings, 
                                    anchoredQuestionIds: updated 
                                  }
                                });
                                setIsDirty(true);
                              }}
                            />
                            <span className="text-muted-foreground font-mono text-xs">
                              Q{idx + 1}:
                            </span>
                            <span className="truncate">{q.question}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="space-y-4">
                {survey.status === "published" ? (
                  <>
                    {/* Published State - Show share link and controls */}
                    <div className="p-5 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                          <Globe className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                        <span className="font-semibold text-green-800 dark:text-green-200">Survey is Live!</span>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                        Share this link with your respondents:
                      </p>
                      <div className="flex gap-2">
                        <Input 
                          readOnly 
                          value={`${window.location.origin}/s/${survey.shareToken}`}
                          className="flex-1 bg-white dark:bg-green-950/50 text-sm"
                        />
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={copyShareLink}
                          className="border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50"
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* View Responses Button */}
                    <Link to={`/survey/${surveyId}/responses`} className="block">
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        View Responses
                      </Button>
                    </Link>
                    
                    <Button 
                      variant="outline" 
                      className="w-full mt-3 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 hover:text-red-700 dark:hover:text-red-300"
                      onClick={() => unpublishMutation.mutate()}
                      disabled={unpublishMutation.isPending}
                    >
                      {unpublishMutation.isPending ? "Unpublishing..." : "Unpublish Survey"}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* Draft State - Show publish button */}
                    <Button 
                      className="w-full" 
                      size="lg"
                      onClick={() => setShowPublishConfirm(true)}
                      disabled={publishMutation.isPending || survey.questions.length === 0}
                    >
                      <Rocket className="h-4 w-4 mr-2" />
                      Publish Survey
                    </Button>
                    {survey.questions.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center">
                        Add at least one question before publishing
                      </p>
                    )}
                  </>
                )}
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => exportMutation.mutate()}
                  disabled={exportMutation.isPending}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export JSON
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => saveChanges({
                    title: survey.title,
                    description: survey.description,
                    questions: survey.questions,
                    settings: survey.settings,
                  })}
                  disabled={isSaving || !isDirty}
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Draft
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

      {/* Publish Confirmation Modal */}
      <AlertDialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Rocket className="h-6 w-6 text-primary" />
            </div>
            <AlertDialogTitle className="text-center">Publish Survey</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              Are you sure you want to publish <strong>"{survey.title}"</strong>? 
              Once published, your survey will be live and accessible to respondents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => publishMutation.mutate()}
              disabled={publishMutation.isPending}
            >
              {publishMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                "Publish Survey"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </AppShell>
  );
}
